<template>
  <section class="container customer-service" aria-labelledby="service-title">
    <div class="service-page-heading">
      <div><h1 id="service-title">客服中心</h1><p>倾听您的需求，协助解决每一个问题</p></div>
      <span class="service-heading-note"><SiteIcon name="chats" :size="18" />咨询与处理记录，随时可查</span>
    </div>
    <div v-if="!loggedIn" class="login-prompt">
      <span class="service-empty-icon"><SiteIcon name="chat-circle-dots" :size="38" /></span>
      <h2>您好，有什么可以帮您？</h2>
      <p>登录后可咨询客服、查看处理结果。</p>
      <button class="primary" @click="$emit('login')">登录并咨询</button>
    </div>
    <div v-else class="service-layout">
      <aside class="service-sidebar" aria-label="咨询分类与记录">
        <div class="service-categories">
          <h2>选择咨询类型</h2>
          <button class="service-category" :class="{ active: !isFinance }" :aria-pressed="!isFinance" :disabled="busy" @click="newSession('GENERAL')">
            <span class="category-icon"><SiteIcon name="chat-circle-dots" :size="24" /></span>
            <span><strong>在线咨询</strong><small>账户、活动与使用帮助</small></span>
            <SiteIcon class="category-arrow" name="arrow-right" :size="16" />
          </button>
          <button class="service-category" :class="{ active: isFinance }" :aria-pressed="isFinance" :disabled="busy" @click="newSession('FINANCE')">
            <span class="category-icon"><SiteIcon name="wallet-outline" :size="24" /></span>
            <span><strong>财务咨询</strong><small>存款、取款与订单问题</small></span>
            <SiteIcon class="category-arrow" name="arrow-right" :size="16" />
          </button>
        </div>
        <div class="service-history">
          <div class="history-heading"><h2>最近咨询</h2><span>{{ sessionTotal }}</span></div>
          <div class="session-list">
            <p v-if="!sessions.length" class="history-empty">{{ loading || loadingSessions ? '正在加载咨询记录…' : '暂无咨询记录' }}<small v-if="!loading && !loadingSessions">发送消息后，可在这里查看进度</small></p>
            <button v-for="item in sessions" :key="item.id" class="session-item" :disabled="busy" :class="{ selected: current?.id === item.id }" :aria-pressed="current?.id === item.id" @click="selectSession(item)">
              <span class="session-item-heading"><b>{{ item.sessionNo }}</b><small :class="{ ongoing: [1, 2].includes(item.status) }">{{ statusText(item.status) }}</small></span>
              <span class="session-description">{{ item.businessOrderNo || item.description }}</span>
            </button>
            <button v-if="sessions.length < sessionTotal" class="history-more" :disabled="loading || loadingSessions" @click="loadSessions(false)">{{ loadingSessions ? '加载中…' : '更多咨询' }}</button>
          </div>
        </div>
        <p class="service-tip"><SiteIcon name="question" :size="18" />描述具体问题并附上相关截图，便于客服为您处理。</p>
      </aside>
      <div class="conversation">
        <header class="conversation-heading">
          <span class="conversation-avatar"><SiteIcon :name="isFinance ? 'wallet-outline' : 'chat-circle-dots'" :size="27" /></span>
          <div class="conversation-title">
            <h2>{{ isFinance ? '财务客服' : '在线客服' }}</h2>
            <p v-if="current">{{ current.sessionNo }} · {{ statusText(current.status) }}</p>
            <p v-else>发送消息，发起您的专属咨询</p>
            <p v-if="orderNo" class="conversation-order">关联订单：{{ orderNo }}</p>
          </div>
          <span class="conversation-badge" :class="{ closed: ended }">{{ current ? statusText(current.status) : '新咨询' }}</span>
        </header>
        <p v-if="error" class="error" role="alert"><span>{{ error }}</span><button type="button" @click="refresh">重试刷新</button></p>
        <div ref="messageList" class="message-list" aria-label="咨询消息" aria-live="polite" :aria-busy="loading">
          <button v-if="messages.length < messageTotal" class="message-history" :disabled="loading" @click="loadOlder">加载历史消息</button>
          <div v-if="!messages.length" class="conversation-empty">
            <span class="service-empty-icon"><SiteIcon name="chats" :size="44" /></span>
            <h3>{{ loading ? '正在加载咨询…' : '您好，有什么可以帮您？' }}</h3>
            <p>请描述您需要咨询的问题<br />客服回复会显示在这里</p>
            <span class="empty-divider">{{ isFinance ? '财务与订单咨询' : '账户 · 活动 · 使用帮助' }}</span>
          </div>
          <article v-for="message in messages" :key="message.id" class="message-bubble" :class="{ mine: message.senderType === 'MEMBER', system: message.senderType === 'SYSTEM' }">
            <small>{{ message.senderType === 'MEMBER' ? '我' : message.senderType === 'SYSTEM' ? '系统' : '客服' }} · {{ formatTime(message.createdAt) }}</small>
            <p v-if="message.content">{{ message.content }}</p>
            <div v-if="message.attachmentUrls?.length" class="message-images"><a v-for="url in message.attachmentUrls" :key="url" :href="url" target="_blank" rel="noopener noreferrer"><img :src="url" alt="消息图片，点击查看原图" /></a></div>
          </article>
        </div>
        <p v-if="ended" class="conversation-ended">本次咨询已结束，发送消息将发起新咨询。</p>
        <form class="composer" @submit.prevent="send">
          <div class="composer-heading"><label for="service-content">咨询内容</label><span>{{ content.length }} / {{ contentLimit }}</span></div>
          <textarea id="service-content" v-model.trim="content" :disabled="busy" :maxlength="contentLimit" required placeholder="请详细描述您的问题，我们会在这里回复您…" />
          <div v-if="attachments.length" class="attachments">
            <div v-for="(url, index) in attachments" :key="url" class="attachment-item"><img :src="url" :alt="`待发送图片 ${index + 1}`" /><button type="button" :disabled="busy" :aria-label="`移除第 ${index + 1} 张图片`" @click="attachments.splice(index, 1)">×</button></div>
          </div>
          <div class="composer-actions">
            <div class="upload-tools">
              <label class="image-upload" :class="{ disabled: busy || attachments.length >= 4 }">
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="3" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m4 17 5-5 4 4 3-3 5 5" /></svg>
                {{ uploading ? '上传中…' : '添加图片' }}
                <input type="file" accept="image/jpeg,image/png" multiple aria-label="添加图片，最多4张，每张5MB" :disabled="busy || attachments.length >= 4" @change="uploadImages" />
              </label>
              <span class="upload-hint">JPG / PNG · 最多4张 · 每张5MB</span>
            </div>
            <button class="primary send-button" :disabled="busy || !content"><span>{{ sending ? '发送中…' : '发送' }}</span><SiteIcon name="arrow-right" :size="18" /></button>
          </div>
        </form>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { customerServiceApi } from '../api'
import { memberSession } from '../api/session'
import SiteIcon from '../components/SiteIcon.vue'

const props = defineProps({ query: { type: String, default: '' } })
defineEmits(['login'])
const loggedIn = computed(() => Boolean(memberSession.value?.token))
const sessions = ref([]), sessionTotal = ref(0), current = ref(null), messages = ref([]), messageTotal = ref(0)
const sessionType = ref('GENERAL'), orderNo = ref(''), content = ref(''), attachments = ref([])
const sending = ref(false), uploading = ref(false), loading = ref(false), error = ref(''), messageList = ref(null)
const loadingSessions = ref(false)
const busy = computed(() => sending.value || uploading.value || loading.value)
const ended = computed(() => current.value && ![1, 2].includes(current.value.status))
const isFinance = computed(() => ['FINANCE', 'DEPOSIT', 'WITHDRAWAL'].includes(sessionType.value))
const contentLimit = computed(() => !current.value || ended.value ? 2000 : 5000)
let page = 1, messagePage = 1, revision = 0, timer, disposed = false, refreshing = false
const statusText = status => ({ 1: '待受理', 2: '处理中', 3: '已完成', 4: '已关闭', 5: '已撤销' })[status] || '未知状态'
const formatTime = seconds => seconds ? new Date(seconds * 1000).toLocaleString('zh-CN', { hour12: false }) : ''
function mergeMessages(rows) {
  const merged = new Map(messages.value.map(item => [item.id, item]))
  rows.forEach(item => merged.set(item.id, item))
  messages.value = [...merged.values()].sort((a, b) => a.id - b.id)
}
async function loadSessions(reset = true, preserveHistory = false) {
  if (loadingSessions.value) return
  loadingSessions.value = true
  const expected = revision, nextPage = reset ? 1 : page + 1
  try {
    // 翻页时同时补齐首屏，避免新咨询插入后偏移分页重复或漏掉新单。
    const [result, head] = await Promise.all([
      customerServiceApi.sessionPage(nextPage),
      reset ? Promise.resolve(null) : customerServiceApi.sessionPage(1),
    ])
    if (expected !== revision || disposed) return
    const existing = reset && !preserveHistory ? [] : sessions.value
    const merged = new Map([...existing, ...(result.data || []), ...(head?.data || [])].map(item => [item.id, item]))
    sessions.value = [...merged.values()].sort((a, b) => b.id - a.id)
    sessionTotal.value = Math.max(Number(result.total) || 0, Number(head?.total) || 0)
    if (!preserveHistory) page = nextPage
  } catch (failure) { if (expected === revision) error.value = failure.message }
  finally { loadingSessions.value = false }
}
async function loadMessages(older = false) {
  if (!current.value) return
  const id = current.value.id, expected = revision, nextPage = older ? messagePage + 1 : 1
  const result = await customerServiceApi.messagePage(id, nextPage)
  if (expected !== revision || disposed) return
  mergeMessages(result.data || [])
  messageTotal.value = Number(result.total) || 0
  if (older) messagePage = nextPage
  else { await nextTick(); if (messageList.value) messageList.value.scrollTop = messageList.value.scrollHeight }
}
async function selectSession(item) {
  if (sending.value || uploading.value) return
  revision += 1
  current.value = item
  sessionType.value = item.sessionType
  orderNo.value = item.businessOrderNo || ''
  messages.value = []; messageTotal.value = 0; messagePage = 1
  content.value = ''; attachments.value = []; error.value = ''
  loading.value = true
  try { await loadMessages() } catch (failure) { error.value = failure.message } finally { loading.value = false }
}
async function newSession(type, order = '') {
  revision += 1
  current.value = null; sessionType.value = type; orderNo.value = order
  messages.value = []; messageTotal.value = 0; messagePage = 1; content.value = ''; attachments.value = []; error.value = ''
  const expected = revision
  loading.value = true
  try {
    for (let number = 1; ; number += 1) {
      const result = await customerServiceApi.sessionPage(number)
      if (expected !== revision || disposed) return
      const item = (result.data || []).find(item => item.sessionType === type && (item.businessOrderNo || '') === order && [1, 2].includes(item.status))
      if (item) { current.value = item; await loadMessages(); break }
      if (!result.data?.length || number * 10 >= Number(result.total)) break
    }
  } catch (failure) { if (expected === revision) error.value = failure.message } finally { if (expected === revision) loading.value = false }
}
async function refresh() {
  if (!loggedIn.value || disposed || busy.value || refreshing) return
  refreshing = true
  const expected = revision
  try {
    await Promise.all([loadMessages(), loadSessions(true, true)])
    if (expected !== revision || disposed) return
    if (!current.value) return
    for (let number = 1; ; number += 1) {
      const result = await customerServiceApi.sessionPage(number)
      if (expected !== revision || disposed) return
      const item = result.data?.find(item => item.id === current.value?.id)
      if (item) { current.value = item; sessions.value = sessions.value.map(row => row.id === item.id ? item : row); break }
      if (!result.data?.length || number * 10 >= Number(result.total)) break
    }
    error.value = ''
  } catch (failure) { if (expected === revision) error.value = failure.message } finally { refreshing = false }
}
async function loadOlder() {
  loading.value = true
  try { await loadMessages(true) } catch (failure) { error.value = failure.message } finally { loading.value = false }
}
async function send() {
  if (busy.value || !content.value) return
  sending.value = true; error.value = ''
  try {
    if (!current.value || ended.value) {
      const session = await customerServiceApi.createSession({ sessionType: sessionType.value, businessOrderNo: orderNo.value, description: content.value, attachmentUrls: [...attachments.value] })
      current.value = session; messages.value = []; messagePage = 1
    } else {
      const message = await customerServiceApi.sendMessage(current.value.id, { content: content.value, attachmentUrls: [...attachments.value] })
      mergeMessages([message])
    }
    content.value = ''; attachments.value = []
    await loadMessages(); await loadSessions()
  } catch (failure) { error.value = failure.message } finally { sending.value = false }
}
async function uploadImages(event) {
  const files = Array.from(event.target.files || [])
  event.target.value = ''
  if (busy.value) return
  if (files.length + attachments.value.length > 4 || files.some(file => !['image/jpeg', 'image/png'].includes(file.type) || file.size > 5 * 1024 * 1024)) { error.value = '最多4张JPG或PNG图片，每张不能超过5MB'; return }
  uploading.value = true
  try { for (const file of files) attachments.value.push((await customerServiceApi.uploadImage(file)).fileUrl) }
  catch (failure) { error.value = failure.message } finally { uploading.value = false }
}
watch([() => memberSession.value?.token, () => props.query], async () => {
  clearInterval(timer); revision += 1
  sessions.value = []; current.value = null; messages.value = []; content.value = ''; attachments.value = []
  if (!loggedIn.value) return
  const params = new URLSearchParams(props.query)
  const type = ({ deposit: 'DEPOSIT', withdrawal: 'WITHDRAWAL', finance: 'FINANCE' })[params.get('type')] || 'GENERAL'
  await newSession(type, params.get('orderNo') || '')
  await loadSessions()
  if (!disposed && loggedIn.value) timer = setInterval(refresh, 5000)
}, { immediate: true })
onBeforeUnmount(() => { disposed = true; revision += 1; clearInterval(timer) })
</script>

<style scoped>
.customer-service { padding: 118px 0 44px; color: #35435a; }
.service-page-heading { display: flex; justify-content: space-between; align-items: center; gap: 20px; margin-bottom: 24px; }
.service-page-heading h1 { margin: 0; font-size: 28px; line-height: 1.4; letter-spacing: .5px; }
.service-page-heading p { margin: 7px 0 0; font-size: 14px; color: #7d8ba2; }
.service-heading-note { display: inline-flex; align-items: center; gap: 8px; color: #8392a9; font-size: 13px; }
.service-layout { display: grid; grid-template-columns: 268px minmax(0, 1fr); align-items: start; gap: 24px; }
.service-sidebar { min-width: 0; overflow: hidden; border: 1px solid #e2e9f3; border-radius: 18px; background: #fff; box-shadow: 0 6px 24px #294a7710; }
.service-sidebar h2 { margin: 0; font-size: 14px; font-weight: 600; }
.service-categories { padding: 24px 16px 20px; }
.service-categories > h2 { margin: 0 8px 16px; }
.service-category { display: flex; align-items: center; width: 100%; gap: 12px; padding: 15px 12px; border: 1px solid transparent; border-radius: 12px; background: transparent; color: #62758f; text-align: left; transition: background .18s, border-color .18s; }
.service-category + .service-category { margin-top: 8px; }
.service-category.active { border-color: #cee5fa; background: linear-gradient(110deg, #edf7ff, #f4faff); color: #1688d7; }
.category-icon { display: grid; flex: 0 0 42px; width: 42px; height: 42px; place-items: center; border-radius: 12px; background: #f0f4fa; }
.active .category-icon { background: #dceffe; }
.service-category > span:nth-child(2) { flex: 1; min-width: 0; }
.service-category strong, .service-category small { display: block; }
.service-category strong { font-size: 16px; font-weight: 600; }
.service-category small { margin-top: 6px; font-size: 11px; color: #8a98ae; white-space: nowrap; }
.category-arrow { flex-shrink: 0; opacity: 0; }
.active .category-arrow { opacity: 1; }
.service-history { border-top: 1px solid #edf1f7; padding: 22px 16px 16px; }
.history-heading { display: flex; align-items: center; justify-content: space-between; margin: 0 8px 14px; }
.history-heading > span { min-width: 22px; padding: 2px 7px; border-radius: 7px; background: #f0f4fa; color: #8a98ad; font-size: 11px; text-align: center; }
.session-list { max-height: 300px; overflow-y: auto; scrollbar-width: thin; scrollbar-color: #d7e1ee transparent; }
.history-empty { padding: 22px 0 28px; margin: 0; text-align: center; color: #8897ad; font-size: 13px; line-height: 1.8; }
.history-empty small { display: block; margin-top: 6px; color: #a0acbd; font-size: 11px; }
.session-item { display: block; width: 100%; padding: 13px 10px; border: 1px solid transparent; border-radius: 10px; background: transparent; text-align: left; }
.session-item + .session-item { margin-top: 5px; }
.session-item.selected { border-color: #d8eafa; background: #f0f8ff; }
.session-item-heading { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.session-item b { min-width: 0; color: #53647d; font-size: 12px; font-weight: 500; overflow-wrap: anywhere; }
.session-item-heading small { flex-shrink: 0; border-radius: 5px; padding: 3px 5px; background: #f0f3f7; color: #8a96a8; font-size: 10px; }
.session-item-heading small.ongoing { background: #e6f3ff; color: #258bd0; }
.session-description { display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 1; overflow: hidden; margin-top: 8px; color: #97a3b5; font-size: 12px; line-height: 1.6; overflow-wrap: anywhere; }
.history-more { width: 100%; padding: 12px; border: 0; background: transparent; color: #258bd0; font-size: 12px; }
.service-tip { display: flex; align-items: flex-start; gap: 8px; margin: 0; padding: 18px 24px; border-top: 1px solid #edf1f7; background: #fbfcfe; color: #91a0b5; font-size: 11px; line-height: 1.8; }
.service-tip .site-icon { flex-shrink: 0; margin-top: 1px; }
.conversation { min-width: 0; border: 1px solid #e1e9f3; border-radius: 18px; background: #fff; box-shadow: 0 6px 24px #294a7710; overflow: hidden; }
.conversation-heading { display: flex; align-items: center; gap: 14px; padding: 22px 28px; border-bottom: 1px solid #edf1f7; }
.conversation-avatar { display: grid; place-items: center; flex: 0 0 48px; height: 48px; border-radius: 15px; color: #fff; background: linear-gradient(140deg, #42bdf0, #258ae0); box-shadow: 0 5px 12px #2997e326; }
.conversation-title { flex: 1; min-width: 0; overflow-wrap: anywhere; }
.conversation-title h2 { margin: 0; font-size: 18px; font-weight: 600; line-height: 1.5; }
.conversation-title p { margin: 5px 0 0; color: #92a0b5; font-size: 12px; line-height: 1.6; }
.conversation-title .conversation-order { color: #5c88ab; }
.conversation-badge { flex-shrink: 0; padding: 6px 11px; border: 1px solid #deedf9; border-radius: 20px; color: #428fca; background: #f3f9fe; font-size: 11px; }
.conversation-badge.closed { color: #929dad; background: #f7f8fa; border-color: #e9edf2; }
.message-list { height: clamp(300px, calc(100dvh - 530px), 420px); overflow: auto; padding: 24px 28px; background: linear-gradient(180deg, #fbfcff, #fff); scrollbar-width: thin; scrollbar-color: #d7e1ee transparent; }
.conversation-empty { display: flex; min-height: 100%; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: 16px 0; }
.service-empty-icon { position: relative; display: grid; width: 88px; height: 88px; place-items: center; color: #65afe7; border-radius: 28px; background: linear-gradient(145deg, #e3f3ff, #f3f9ff); }
.service-empty-icon::after { content: ''; position: absolute; width: 12px; height: 12px; right: -5px; top: 7px; border: 3px solid #fff; border-radius: 50%; background: #abd9f8; }
.conversation-empty h3 { margin: 22px 0 10px; font-size: 18px; font-weight: 500; color: #526780; }
.conversation-empty p { margin: 0; color: #94a2b6; font-size: 13px; line-height: 1.9; }
.empty-divider { display: flex; align-items: center; gap: 12px; margin-top: 20px; color: #a8b3c3; font-size: 11px; }
.empty-divider::before, .empty-divider::after { content: ''; width: 26px; height: 1px; background: #e4ebf4; }
.message-history { display: block; margin: 0 auto 20px; padding: 7px 14px; border: 1px solid #e6edf5; border-radius: 20px; background: #fff; color: #8597af; font-size: 12px; }
.message-bubble { width: fit-content; max-width: 85%; padding: 13px 16px; margin-bottom: 18px; border: 1px solid #e9eef5; border-radius: 3px 14px 14px; background: #f3f6fb; overflow-wrap: anywhere; }
.message-bubble.mine { margin-left: auto; border-color: #dbeefa; border-radius: 14px 3px 14px 14px; background: #eaf6ff; }
.message-bubble.system { margin-inline: auto; background: #fafbfc; color: #8b98ab; }
.message-bubble > small { font-size: 11px; color: #91a0b5; line-height: 1.7; }
.message-bubble p { margin: 7px 0 0; white-space: pre-wrap; font-size: 14px; line-height: 1.8; }
.message-images { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
.message-images a { display: block; border: 1px solid #dce7f2; border-radius: 8px; overflow: hidden; background: #fff; }
.message-images img { display: block; width: 110px; height: 100px; max-width: 100%; object-fit: contain; }
.conversation-ended { margin: 0; padding: 12px 28px; background: #f7f9fc; color: #8a99ae; font-size: 12px; line-height: 1.7; border-top: 1px solid #edf1f7; }
.composer { padding: 20px 28px 24px; border-top: 1px solid #edf1f7; background: #fff; }
.composer-heading { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 12px; }
.composer-heading label { font-size: 13px; font-weight: 600; color: #657790; }
.composer-heading span { font-size: 11px; color: #a1adbd; }
.composer textarea { display: block; width: 100%; min-height: 92px; max-height: 240px; padding: 13px 15px; border: 1px solid #e4ebf4; border-radius: 10px; background: #f9fbfe; color: #425873; font: inherit; font-size: 13px; line-height: 1.7; resize: vertical; transition: border-color .18s, box-shadow .18s; }
.composer textarea::placeholder { color: #a0adbf; }
.composer textarea:focus { outline: 0; border-color: #78b9e9; box-shadow: 0 0 0 3px #e7f3fd; background: #fff; }
.composer-actions { display: flex; justify-content: space-between; align-items: center; gap: 16px; margin-top: 16px; }
.upload-tools { display: flex; align-items: center; gap: 12px; min-width: 0; }
.image-upload { position: relative; display: inline-flex; align-items: center; flex-shrink: 0; gap: 7px; padding: 8px 0; color: #7489a3; font-size: 12px; cursor: pointer; }
.image-upload input { position: absolute; width: 1px; height: 1px; padding: 0; opacity: 0; overflow: hidden; clip-path: inset(50%); }
.image-upload.disabled { opacity: .55; cursor: not-allowed; }
.upload-hint { font-size: 10px; color: #a2afc0; }
.composer .send-button { display: flex; justify-content: center; align-items: center; gap: 18px; min-width: 112px; height: 42px; padding: 0 20px; border: 0; border-radius: 9px; background: linear-gradient(110deg, #2193e3, #39b8ef); box-shadow: 0 4px 10px #2193e324; font-size: 14px; }
.composer .send-button:disabled { background: #aecfe8; box-shadow: none; opacity: 1; }
.attachments { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 16px; }
.attachment-item { position: relative; width: 72px; height: 72px; padding: 4px; border: 1px solid #e1e9f3; border-radius: 9px; background: #f9fbfe; }
.attachment-item img { width: 100%; height: 100%; object-fit: contain; border-radius: 5px; }
.attachment-item button { position: absolute; top: -6px; right: -6px; display: grid; width: 22px; height: 22px; place-items: center; padding: 0; border: 2px solid #fff; border-radius: 50%; background: #8194ad; color: #fff; font-size: 16px; line-height: 1; }
.error { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin: 0; padding: 12px 28px; background: #fff4f2; color: #ba584c; font-size: 13px; line-height: 1.6; overflow-wrap: anywhere; }
.error button { flex-shrink: 0; padding: 4px 0; border: 0; background: transparent; color: #a9473b; font-size: 12px; text-decoration: underline; text-underline-offset: 3px; }
.login-prompt { display: flex; min-height: 480px; flex-direction: column; align-items: center; justify-content: center; padding: 36px 24px; border: 1px solid #e1e9f3; border-radius: 18px; background: #fff; text-align: center; box-shadow: 0 6px 24px #294a7710; }
.login-prompt h2 { margin: 26px 0 10px; color: #526780; font-size: 22px; font-weight: 500; }
.login-prompt p { margin: 0 0 28px; color: #94a2b6; font-size: 14px; line-height: 1.8; }
.login-prompt .primary { padding: 12px 30px; border-radius: 9px; font-size: 14px; }
button:disabled { opacity: .55; cursor: not-allowed; }
button:focus-visible, .image-upload:focus-within, .message-images a:focus-visible { outline: 2px solid #299ce5; outline-offset: 3px; }
@media (hover: hover) {
  .service-category:not(:disabled):hover, .session-item:not(:disabled):hover { background: #f1f7fd; }
  .image-upload:not(.disabled):hover, .history-more:not(:disabled):hover { color: #1688d7; }
  .send-button:not(:disabled):hover { filter: brightness(1.04); }
}
@media (max-width: 1050px) {
  .service-layout { grid-template-columns: 248px minmax(0, 1fr); gap: 20px; }
  .upload-tools { align-items: flex-start; flex-direction: column; gap: 0; }
  .conversation-heading, .composer { padding-inline: 22px; }
}
@media (max-width: 767px) {
  .customer-service { width: calc(100% - 28px); padding: 94px 0 28px; }
  .service-page-heading { margin-bottom: 20px; }
  .service-page-heading h1 { font-size: 24px; }
  .service-page-heading p { font-size: 12px; }
  .service-heading-note { display: none; }
  .service-layout { grid-template-columns: minmax(0, 1fr); gap: 16px; }
  .service-categories { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; padding: 18px 14px 14px; }
  .service-categories > h2 { grid-column: 1 / -1; margin: 0 3px 2px; font-size: 13px; }
  .service-category { gap: 8px; padding: 12px 8px; }
  .service-category + .service-category { margin: 0; }
  .category-icon { width: 32px; height: 36px; flex-basis: 32px; border-radius: 9px; }
  .service-category strong { font-size: 14px; }
  .service-category small { font-size: 10px; white-space: normal; line-height: 1.6; }
  .category-arrow { display: none; }
  .service-history { padding: 14px; }
  .history-heading { margin: 0 3px 8px; }
  .history-heading h2 { font-size: 13px; }
  .session-list { max-height: 155px; }
  .history-empty { padding: 8px 0 2px; font-size: 12px; }
  .history-empty small { display: inline; margin-left: 8px; font-size: 10px; }
  .service-tip { display: none; }
  .conversation-heading { gap: 12px; padding: 18px; }
  .conversation-avatar { flex-basis: 42px; height: 42px; border-radius: 12px; }
  .conversation-title h2 { font-size: 16px; }
  .conversation-title p { font-size: 11px; }
  .conversation-badge { padding: 5px 8px; font-size: 10px; }
  .message-list { height: 320px; padding: 20px 16px; }
  .conversation-empty h3 { font-size: 17px; }
  .message-bubble { max-width: 92%; padding: 12px; }
  .message-bubble p { font-size: 13px; }
  .composer { padding: 18px 16px; }
  .composer textarea { min-height: 100px; }
  .composer-actions { gap: 10px; margin-top: 12px; }
  .composer .send-button { min-width: 92px; gap: 12px; padding-inline: 14px; }
  .upload-hint { font-size: 9px; line-height: 1.6; }
  .conversation-ended, .error { padding: 12px 18px; }
  .login-prompt { min-height: 400px; }
  .login-prompt h2 { font-size: 19px; }
}
@media (prefers-reduced-motion: reduce) {
  .service-category, .composer textarea { transition: none; }
}
</style>
